import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { buildVocabQuestions } from "@/lib/vocabQuiz";
import Quiz from "@/components/Quiz";
import WordCard from "@/components/WordCard";
import ConfettiBurst from "@/components/ConfettiBurst";
import {
  Minus,
  Plus,
  RotateCcw,
  ArrowLeft,
  ArrowRight,
  X,
  PartyPopper,
  ChevronDown,
  Volume2,
} from "lucide-react";
import { t } from "@/i18n";

/* Prononciation via l'API navigateur SpeechSynthesis — pas de backend, pas
   de coût, fonctionne hors-ligne sur la plupart des navigateurs. */

/* ───────────────────────────────────────────────────────────
 * Fiche d'un mot — mot arabe, translittération, traduction et
 * racine. Sert à deux moments : sur l'écran de découverte avant
 * le quiz, et pendant le quiz quand on sèche.
 * ─────────────────────────────────────────────────────────── */

/* Mêmes bornes que Quiz.jsx : hauteur exacte du header mobile (h-16) et
   de la barre d'onglets du bas, pour ne jamais passer derrière elles. */
const NAV_TOP = "4rem";
const NAV_BOTTOM = "calc(3.4rem + env(safe-area-inset-bottom))";

/* Écran de découverte : tous les mots de la session sur une seule page,
   contrainte à l'écran (pas de défilement de la page elle-même). */
function WordsOverviewScreen({ words, onDone, onExit }) {
  return (
    <div
      className="fixed inset-x-0 z-20 bg-background flex flex-col md:static md:z-auto"
      style={{ top: NAV_TOP, bottom: NAV_BOTTOM }}
    >
      <div className="shrink-0 px-4 pt-3 pb-2 flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-primary">
            {t("vocabquizsession.decouverte_titre", {
              count: words.length,
              s: words.length > 1 ? "s" : "",
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("vocabquizsession.regarde_ces_mots_avant_de_t")}
          </p>
        </div>
        <button
          onClick={onExit}
          aria-label={t("alphabetlearnscreen.quitter")}
          className="shrink-0 w-8 h-8 rounded-full border grid place-items-center hover:bg-secondary transition"
        >
          <X size={15} />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 space-y-2">
        {words.map((w) => (
          <div key={w.id} className="rounded-2xl border bg-card p-3">
            <WordCard word={w} />
          </div>
        ))}
        <div className="h-2" />
      </div>
      <div className="shrink-0 border-t bg-background/95 backdrop-blur-xl p-3">
        <button
          onClick={onDone}
          className="w-full max-w-md mx-auto py-3 rounded-2xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2"
        >
          {t("practicesentences.commencer_l_entrainement")}{" "}
          <ArrowRight size={17} />
        </button>
      </div>
    </div>
  );
}

const shuffle = (arr) => [...arr].sort(() => 0.5 - Math.random());

// Doit rester aligné avec VOCAB_MASTERY_TARGET côté backend (progression.py).
const VOCAB_MASTERY_TARGET = 3;

/* ───────────────────────────────────────────────────────────
 * Sélection intelligente des mots d'une session.
 * ─────────────────────────────────────────────────────────── */
function smartSample(words, count, { capNew = true } = {}) {
  const isFresh = (w) => (w.seen || 0) === 0;
  const isMastered = (w) => !!w.mastered;
  const inProgress = shuffle(
    words.filter((w) => !isFresh(w) && !isMastered(w)),
  );
  const fresh = shuffle(words.filter(isFresh));
  const mastered = shuffle(words.filter(isMastered));
  const newCap = capNew ? Math.max(3, Math.round(count * 0.4)) : count;
  const picked = [];
  const pickedIds = new Set();
  const take = (w) => {
    if (picked.length >= count || pickedIds.has(w.id)) return;
    picked.push(w);
    pickedIds.add(w.id);
  };
  for (const w of inProgress) {
    if (picked.length >= count) break;
    take(w);
  }
  let introduced = 0;
  for (const w of fresh) {
    if (picked.length >= count || introduced >= newCap) break;
    take(w);
    introduced++;
  }
  const leftovers = shuffle([...mastered, ...fresh, ...inProgress]);
  for (const w of leftovers) {
    if (picked.length >= count) break;
    take(w);
  }
  return shuffle(picked);
}

export default function VocabQuizSession() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState([]);
  const [count, setCount] = useState(10);
  const [mode, setMode] = useState("ar-fr"); // ar-fr | fr-ar | mixed
  const [seenFilter, setSeenFilter] = useState("all"); // all | new | seen
  const [showLearn, setShowLearn] = useState(true);
  const [notice, setNotice] = useState("");
  const [pool, setPool] = useState([]);
  const [overviewWords, setOverviewWords] = useState(null);
  const [session, setSession] = useState(null);
  const [done, setDone] = useState(null);
  const [hintWord, setHintWord] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [loadingGroups, setLoadingGroups] = useState(true);

  // État d'expansion pour la catégorie mère
  const [expandedCategory, setExpandedCategory] = useState(null);
  // État d'expansion pour la sous-catégorie (le groupe)
  const [expandedGroupId, setExpandedGroupId] = useState(null);

  const [groupWordsCache, setGroupWordsCache] = useState({});
  const [loadingWordsFor, setLoadingWordsFor] = useState(null);
  const [selectedWordIds, setSelectedWordIds] = useState(() => new Set());

  useEffect(() => {
    api
      .get("/progression/vocab-groups")
      .then((r) => setGroups(r.data?.groups || []))
      .catch(() => {})
      .finally(() => setLoadingGroups(false));
  }, []);

  useEffect(() => {
    const gid = searchParams.get("group");
    if (!gid || groups.length === 0) return;
    setSelected((s) => (s.includes(gid) ? s : [...s, gid]));
  }, [groups, searchParams]);

  // Regroupement des groupes par catégorie mère
  const groupedCategories = useMemo(() => {
    const map = {};
    groups.forEach((g) => {
      const catName = g.category || "Général";
      if (!map[catName]) map[catName] = [];
      map[catName].push(g);
    });
    return map;
  }, [groups]);

  const toggle = (id) =>
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );

  const all = () =>
    setSelected(
      selected.length === groups.length ? [] : groups.map((g) => g.id),
    );

  const wordById = useMemo(() => {
    const m = {};
    for (const w of pool) m[w.id] = w;
    return m;
  }, [pool]);

  const allCachedWords = useMemo(() => {
    const m = {};
    for (const gid of Object.keys(groupWordsCache)) {
      for (const w of groupWordsCache[gid]) m[w.id] = w;
    }
    return m;
  }, [groupWordsCache]);

  const toggleExpandGroup = async (gid, e) => {
    e.stopPropagation();
    if (expandedGroupId === gid) {
      setExpandedGroupId(null);
      return;
    }
    setExpandedGroupId(gid);
    if (!groupWordsCache[gid]) {
      setLoadingWordsFor(gid);
      try {
        const r = await api.get(`/progression/vocab-groups/${gid}/words`);
        setGroupWordsCache((c) => ({ ...c, [gid]: r.data?.words || [] }));
      } catch {
      } finally {
        setLoadingWordsFor(null);
      }
    }
  };

  const sortedGroupWords = (gid) => {
    const words = groupWordsCache[gid] || [];
    return [...words].sort((a, b) => (b.seen || 0) - (a.seen || 0));
  };

  const groupSelectedCount = (gid) => {
    const words = groupWordsCache[gid];
    if (!words) return 0;
    return words.reduce((n, w) => n + (selectedWordIds.has(w.id) ? 1 : 0), 0);
  };

  const toggleWord = (id) => {
    setSelectedWordIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const start = async () => {
    setNotice("");
    if (selectedWordIds.size > 0) {
      const chosen = Object.values(allCachedWords).filter((w) =>
        selectedWordIds.has(w.id),
      );
      if (chosen.length < 4) {
        setNotice(t("vocabquizsession.notice_selectionne_au_moins_4_mots"));
        return;
      }
      const widePool = Object.values(allCachedWords);
      setPool(widePool);
      const sample = shuffle(chosen).slice(0, Math.min(count, chosen.length));
      setRetryKey((k) => k + 1);
      setDone(null);
      if (showLearn) {
        setOverviewWords(sample);
        setSession(null);
      } else {
        setOverviewWords(null);
        setSession(
          buildVocabQuestions(
            sample,
            mode,
            widePool.length >= 6
              ? widePool
              : widePool.concat(widePool, widePool),
          ),
        );
      }
      return;
    }
    try {
      const results = await Promise.all(
        selected.map((gid) =>
          api.get(`/progression/vocab-groups/${gid}/words`),
        ),
      );
      let combined = [];
      for (const r of results) combined = combined.concat(r.data?.words || []);
      const seen = new Set();
      combined = combined.filter((w) =>
        seen.has(w.id) ? false : (seen.add(w.id), true),
      );
      setPool(combined);
      let filtered = combined;
      if (seenFilter === "new")
        filtered = combined.filter((w) => (w.seen || 0) === 0);
      if (seenFilter === "seen")
        filtered = combined.filter((w) => (w.seen || 0) > 0);
      if (filtered.length < 4) {
        setNotice(
          seenFilter === "all"
            ? t("vocabquizsession.notice_pas_assez_mots_tous")
            : t("vocabquizsession.notice_pas_assez_mots_filtre"),
        );
        return;
      }
      const sample = smartSample(filtered, Math.min(count, filtered.length), {
        capNew: seenFilter === "all",
      });
      setRetryKey((k) => k + 1);
      setDone(null);
      if (showLearn) {
        setOverviewWords(sample);
        setSession(null);
      } else {
        setOverviewWords(null);
        setSession(
          buildVocabQuestions(
            sample,
            mode,
            combined.length >= 6
              ? combined
              : combined.concat(combined, combined),
          ),
        );
      }
    } catch {
      setNotice(t("vocabquizsession.notice_seance_non_demarree"));
    }
  };

  const launchFromOverview = () => {
    const wide = pool.length >= 6 ? pool : pool.concat(pool, pool);
    setSession(buildVocabQuestions(overviewWords, mode, wide));
    setOverviewWords(null);
  };

  const backToSettings = () => {
    setSession(null);
    setOverviewWords(null);
    setDone(null);
  };

  // ── Écrans (Découverte, Quiz, Fin) ──
  if (overviewWords) {
    return (
      <WordsOverviewScreen
        words={overviewWords}
        onDone={launchFromOverview}
        onExit={backToSettings}
      />
    );
  }

  if (session && !done) {
    return (
      <>
        <Quiz
          key={retryKey}
          questions={session}
          onDone={(r) => setDone(r)}
          rewardMode="normal"
          rewardAmount={1}
          arabicPrompt={mode === "fr-ar"}
          arabicOptions={mode === "fr-ar"}
          onHint={(q) => setHintWord(wordById[q.ref] || null)}
          onAnswer={(q, good) => {
            if (q.ref) {
              api
                .post("/progression/vocab/answer", {
                  word_id: q.ref,
                  correct: good,
                })
                .catch(() => {});
            }
          }}
          showCelebration={false}
          title={t("dashboard.mots")}
          onExit={backToSettings}
        />
        {hintWord && (
          <div
            className="fixed inset-0 z-[70] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setHintWord(null)}
          >
            <div
              className="w-full max-w-sm rounded-3xl border bg-card p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-end mb-1">
                <button
                  onClick={() => setHintWord(null)}
                  className="rounded-full p-1.5 hover:bg-muted"
                >
                  <X size={17} />
                </button>
              </div>
              <WordCard word={hintWord} />
              <button
                onClick={() => setHintWord(null)}
                className="w-full mt-5 py-3 rounded-2xl bg-primary text-primary-foreground font-bold"
              >
                {t("exerciseblock.j_ai_compris")}
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (done) {
    const score = Math.round((done.correct / done.total) * 100);
    const passed = score >= 70;
    const earned = done.earned ?? 0;
    return (
      <div
        className="max-w-xl mx-auto text-center pb-24"
        data-testid="vocab-result"
      >
        <div className="rounded-3xl border bg-card p-7 relative overflow-hidden">
          {earned > 0 && <ConfettiBurst />}
          <PartyPopper size={44} className="mx-auto mb-3 text-amber-500" />
          <h2 className="font-head text-2xl font-bold mb-1">
            {t("quiz.exercice_termine")}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            {done.correct} / {done.total} {t("myerrors.bonnes_reponses")}{" "}
            {score}%
          </p>
          <div className="text-5xl font-head font-extrabold text-amber-500 mb-1">
            +{earned} 🪙
          </div>
          <p className="text-xs text-muted-foreground mb-6">
            {earned > 0
              ? t("quiz.gagnes_sur_cet_exercice")
              : t("quiz.aucun_dinar_cette_fois_retente")}
          </p>
          <div className="h-2.5 rounded-full bg-secondary overflow-hidden mb-6">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${score}%`,
                backgroundColor: passed ? "#16A34A" : "hsl(var(--destructive))",
              }}
            />
          </div>
          <button
            onClick={backToSettings}
            className="w-full py-3.5 rounded-2xl bg-primary text-primary-foreground font-bold mb-2.5"
          >
            {t("exerciseblock.continuer")}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => {
                const wide = pool.length >= 6 ? pool : pool.concat(pool, pool);
                const words =
                  overviewWords ||
                  pool.filter((w) => session.some((q) => q.key === w.id));
                setDone(null);
                setRetryKey((k) => k + 1);
                setSession(buildVocabQuestions(words, mode, wide));
              }}
              className="py-3 rounded-2xl border font-semibold flex items-center justify-center gap-2"
            >
              <RotateCcw size={15} /> {t("myerrors.recommencer")}
            </button>
            <button
              onClick={() => navigate("/practice")}
              className="py-3 rounded-2xl border font-semibold flex items-center justify-center gap-2"
            >
              <ArrowLeft size={15} /> {t("coursequestions.accueil")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Réglages ──
  return (
    <div className="max-w-2xl mx-auto pb-40">
      <h1 className="font-head text-2xl font-bold mb-1">
        {t("roadmap.vocabulaire")}
      </h1>
      <p className="text-sm text-muted-foreground mb-5">
        {t("vocabquizsession.choisis_tes_groupes_puis_lance_toi")}
      </p>

      {notice && (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-3 mb-4 text-sm text-amber-800 dark:text-amber-300">
          {notice}
        </div>
      )}

      {/* Réglages compacts */}
      <div className="rounded-2xl border bg-card p-4 mb-4 space-y-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">
            {t("vocabquizsession.sens")}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              ["mixed", t("vocabquizsession.mode_mixte")],
              ["ar-fr", t("vocabquizsession.mode_latin_arabe")],
              ["fr-ar", t("vocabquizsession.mode_arabe_latin")],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                className={`rounded-xl border-2 p-2.5 text-center transition ${
                  mode === id
                    ? "border-primary bg-primary/10"
                    : "hover:border-primary/40"
                }`}
              >
                <div className="text-sm font-semibold leading-tight">
                  {label}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">
            {t("vocabquizsession.quels_mots")}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              ["all", t("vocabquizsession.filtre_tous")],
              ["new", t("vocabquizsession.filtre_jamais_vus")],
              ["seen", t("vocabquizsession.filtre_deja_vus")],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setSeenFilter(id)}
                className={`rounded-xl border-2 p-2.5 text-center transition ${
                  seenFilter === id
                    ? "border-primary bg-primary/10"
                    : "hover:border-primary/40"
                }`}
              >
                <div className="text-sm font-semibold leading-tight">
                  {label}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium">{t("dashboard.mots")}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCount((c) => Math.max(5, c - 5))}
              className="w-9 h-9 rounded-lg border grid place-items-center"
            >
              <Minus size={15} />
            </button>
            <span className="font-head text-lg font-bold w-8 text-center">
              {count}
            </span>
            <button
              onClick={() => setCount((c) => c + 5)}
              className="w-9 h-9 rounded-lg border grid place-items-center"
            >
              <Plus size={15} />
            </button>
          </div>
        </div>

        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <span className="text-sm">
            <span className="font-medium">
              {t("vocabquizsession.voir_les_mots_avant")}
            </span>
            <span className="block text-xs text-muted-foreground">
              {t("vocabquizsession.mot_traduction_et_racine_sur_une")}
            </span>
          </span>
          <input
            type="checkbox"
            checked={showLearn}
            onChange={(e) => setShowLearn(e.target.checked)}
            className="w-5 h-5 accent-primary shrink-0"
          />
        </label>
      </div>

      {/* Hiérarchie Catégories > Sous-catégories > Mots */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
          {t("vocabquizsession.groupes_de_mots")}
        </span>
        {groups.length > 0 && (
          <button
            onClick={all}
            className="text-sm text-primary hover:underline"
          >
            {selected.length === groups.length
              ? t("practicealphabet.aucun")
              : t("practicealphabet.tout")}
          </button>
        )}
      </div>

      {selectedWordIds.size > 0 && (
        <div className="rounded-2xl border border-primary/40 bg-primary/5 p-3 mb-3 flex items-center justify-between gap-3">
          <span className="text-sm font-medium">
            {t("vocabquizsession.mot_s", {
              count: selectedWordIds.size,
              s: selectedWordIds.size > 1 ? "s" : "",
            })}{" "}
            {t("vocabquizsession.a_la_main_remplace_la_selection")}
          </span>
          <button
            onClick={() => setSelectedWordIds(new Set())}
            className="text-sm text-primary hover:underline shrink-0"
          >
            {t("practicealphabet.reinitialiser")}
          </button>
        </div>
      )}

      {loadingGroups ? (
        <div className="text-sm text-muted-foreground py-6 text-center">
          {t("vocabquizsession.chargement_des_groupes")}
        </div>
      ) : groups.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">
          {t("vocabquizsession.aucun_groupe_de_vocabulaire_disponible_pour")}
        </div>
      ) : (
        <div className="space-y-3 mb-4">
          {Object.entries(groupedCategories).map(([catName, catGroups]) => {
            const isCatExpanded = expandedCategory === catName;

            return (
              <div
                key={catName}
                className="rounded-2xl border-2 overflow-hidden bg-card"
              >
                {/* En-tête Catégorie Mère */}
                <div className="flex items-stretch border-b last:border-b-0 bg-secondary/5 hover:bg-secondary/10 transition">
                  <button
                    onClick={() =>
                      setExpandedCategory(isCatExpanded ? null : catName)
                    }
                    className="flex-1 p-3 text-left flex justify-between items-center"
                  >
                    <span className="font-bold text-base">{catName}</span>
                    <span className="text-xs font-medium text-muted-foreground bg-background px-2 py-1 rounded-md border">
                      {catGroups.length} {t("vocabquizsession.sous_categorie")}
                    </span>
                  </button>
                  <button
                    onClick={() =>
                      setExpandedCategory(isCatExpanded ? null : catName)
                    }
                    className="shrink-0 w-12 grid place-items-center border-l hover:bg-secondary/20 transition"
                  >
                    <ChevronDown
                      size={18}
                      className={`transition-transform ${isCatExpanded ? "rotate-180" : ""}`}
                    />
                  </button>
                </div>

                {/* Sous-catégories (Anciennement "Groupes") */}
                {isCatExpanded && (
                  <div className="p-2 space-y-2 bg-secondary/5">
                    {catGroups.map((g) => {
                      const on = selected.includes(g.id);
                      const expanded = expandedGroupId === g.id;
                      const chosenHere = groupSelectedCount(g.id);

                      return (
                        <div
                          key={g.id}
                          className={`rounded-2xl border-2 overflow-hidden transition bg-card ${
                            on ? "bg-amber-500/10" : "hover:border-amber-500/40"
                          }`}
                          style={on ? { borderColor: "#CA8A04" } : {}}
                        >
                          <div className="flex items-stretch">
                            <button
                              onClick={() => toggle(g.id)}
                              className="flex-1 min-w-0 text-left p-3"
                            >
                              <div className="flex items-center justify-between gap-2 mb-1.5">
                                <span className="font-medium text-sm truncate">
                                  {g.sous_categorie || g.title}
                                </span>
                                <span className="text-[11px] font-bold text-muted-foreground shrink-0 tabular-nums">
                                  {g.pct}%
                                </span>
                              </div>
                              <div className="h-1.5 rounded-full bg-secondary overflow-hidden mb-1.5">
                                <div
                                  className="h-full rounded-full bg-emerald-500 transition-all"
                                  style={{ width: `${g.pct}%` }}
                                />
                              </div>
                              <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                                <span>
                                  {g.mastered}/{g.total}{" "}
                                  {t("vocabquizsession.mots_maitrises")}
                                </span>
                                {chosenHere > 0 && (
                                  <span className="text-primary font-semibold">
                                    {t("vocabquizsession.choisi_s", {
                                      count: chosenHere,
                                      s: chosenHere > 1 ? "s" : "",
                                    })}
                                  </span>
                                )}
                              </div>
                            </button>
                            <button
                              onClick={(e) => toggleExpandGroup(g.id, e)}
                              aria-label={t(
                                "vocabquizsession.voir_les_mots_du_groupe",
                              )}
                              className="shrink-0 w-10 grid place-items-center border-l hover:bg-secondary/20 transition"
                            >
                              <ChevronDown
                                size={16}
                                className={`transition-transform ${expanded ? "rotate-180" : ""}`}
                              />
                            </button>
                          </div>

                          {/* Mots (Troisième niveau) */}
                          {expanded && (
                            <div className="border-t p-2 space-y-1.5 max-h-72 overflow-y-auto">
                              {loadingWordsFor === g.id ? (
                                <div className="text-xs text-muted-foreground text-center py-3">
                                  {t("lessoneditor.chargement")}
                                </div>
                              ) : sortedGroupWords(g.id).length === 0 ? (
                                <div className="text-xs text-muted-foreground text-center py-3">
                                  {t("progress.aucun_mot_dans_ce_groupe")}
                                </div>
                              ) : (
                                sortedGroupWords(g.id).map((w) => (
                                  <label
                                    key={w.id}
                                    className="flex items-center gap-2 rounded-xl p-2 hover:bg-secondary/40 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      className="w-4 h-4 accent-primary shrink-0"
                                      checked={selectedWordIds.has(w.id)}
                                      onChange={() => toggleWord(w.id)}
                                    />
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center justify-between gap-2">
                                        <span
                                          className="arabic text-base truncate"
                                          dir="rtl"
                                        >
                                          {w.word_ar}
                                        </span>
                                        <span className="text-[10px] text-muted-foreground shrink-0 text-right">
                                          {w.mastered
                                            ? t("practicealphabet.maitrise")
                                            : t(
                                                "vocabquizsession.bonnes_recues",
                                                {
                                                  count: w.correct || 0,
                                                  target: VOCAB_MASTERY_TARGET,
                                                },
                                              )}
                                          <br />
                                          {t("vocabquizsession.nb_fois", {
                                            count: w.seen || 0,
                                          })}
                                        </span>
                                      </div>
                                      <div className="text-xs text-muted-foreground truncate">
                                        {w.translation_en}
                                      </div>
                                      <div className="h-1 rounded-full bg-secondary overflow-hidden mt-1">
                                        <div
                                          className="h-full rounded-full bg-emerald-500"
                                          style={{
                                            width: `${Math.min(100, ((w.correct || 0) / VOCAB_MASTERY_TARGET) * 100)}%`,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </label>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bouton de validation toujours visible */}
      <div
        className="fixed bottom-0 inset-x-0 z-30 border-t bg-background/95 backdrop-blur-xl px-4 pt-3"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 4.5rem)" }}
      >
        <div className="max-w-2xl mx-auto">
          <button
            disabled={selected.length === 0 && selectedWordIds.size === 0}
            onClick={start}
            className="w-full py-4 rounded-2xl bg-primary text-primary-foreground font-bold disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {selectedWordIds.size > 0
              ? t("vocabquizsession.commencer_avec_nb_mots", {
                  count: selectedWordIds.size,
                  s: selectedWordIds.size > 1 ? "s" : "",
                })
              : selected.length === 0
                ? t("vocabquizsession.choisis_un_groupe")
                : t("alphabetlearnscreen.suivant")}{" "}
            <ArrowRight size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}
